import React,{ forwardRef, useMemo } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { Box } from '@mui/material';
import { ChartDataItem } from '../types';

interface CenteredMetricProps {
    centerX: number;
    centerY: number;
    value: number;
    label?: string;
}

const CenteredMetric = ({ centerX, centerY, value, label }: CenteredMetricProps) => (
    <>
        <text
            x={centerX}
            y={centerY - 10}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
                fontSize: '2rem',
                fontWeight: 700,
            }}
        >
            {value}%
        </text>
        {label && (
            <text
                x={centerX}
                y={centerY + 20}
                textAnchor="middle"
                dominantBaseline="central"
                style={{
                    fontSize: '0.875rem',
                    fill: '#666',
                }}
            >
                {label}
            </text>
        )}
    </>
);

interface DonutChartProps {
    data: ChartDataItem[];
    percentage: number;
    label?: string;
    colors?: { scheme: string };
    height?: string | number;
    interactive?: boolean;
}

export const DonutChart = forwardRef<HTMLDivElement, DonutChartProps>(
    (
        {
            data,
            percentage,
            label,
            colors = { scheme: 'nivo' },
            height = '100%',
            interactive = false,
        },
        ref
    ) => {
        const layers = useMemo(
            () => [
                'arcs',
                'arcLabels',
                'arcLinkLabels',
                'legends',
                (props: { centerX: number; centerY: number }) => (
                    <CenteredMetric
                        key="centered-metric"
                        centerX={props.centerX}
                        centerY={props.centerY}
                        value={percentage}
                        label={label}
                    />
                ),
            ],
            [percentage, label]
        );

        return (
            <Box ref={ref} sx={{ height, width: '100%' }}>
                <ResponsivePie
                    data={data}
                    margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                    innerRadius={0.7}
                    padAngle={0.7}
                    cornerRadius={3}
                    activeOuterRadiusOffset={8}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                    colors={colors}
                    enableArcLinkLabels={false}
                    enableArcLabels={false}
                    isInteractive={interactive}
                    layers={layers}
                    animate
                    motionConfig="gentle"
                />
            </Box>
        );
    }
);

DonutChart.displayName = 'DonutChart';
